import type { operations } from '@molchanovs/api-contract';
import { z } from 'zod';
import { api } from './client';
import { isApiError } from './errors';

type AiSuccess =
  operations['postApiV1ApplicationAiResponses']['responses'][201]['content']['application/json'];
export type ArticleTitleSuggestion = {
  data: Extract<AiSuccess['data'], { useCase: 'helpdesk_article_title' }>;
};
export type ArticleGenerationAdmission =
  operations['postApiV1ApplicationAiResponses']['responses'][202]['content']['application/json'];
export type ArticleGenerationStatus = Extract<
  operations['getApiV1TasksByTaskUuid']['responses'][200]['content']['application/json'],
  { data: { articleGeneration: unknown } }
>;
const id = z.uuid();
const key = z.string().min(8).max(200);
const content = z
  .string()
  .min(1)
  .max(100000)
  .refine((text) => text.trim().length > 0 && !text.includes('\0'));
const titleInput = z.object({ content, articleId: id.optional() }).strict();
const titleSchema: z.ZodType<ArticleTitleSuggestion> = z.object({
  data: z.object({
    id,
    useCase: z.literal('helpdesk_article_title'),
    state: z.literal('succeeded'),
    output: z.object({
      title: z
        .string()
        .min(1)
        .max(500)
        .refine((text) => text.trim().length > 0 && !text.includes('\0')),
      basis: z.object({
        articleId: id.nullable(),
        articleEtag: z.string().nullable(),
        contentSha256: z.string().regex(/^[a-f0-9]{64}$/u),
      }),
      currentAtCompletion: z.boolean().nullable(),
      saved: z.literal(false),
    }),
  }),
});
const admissionSchema: z.ZodType<ArticleGenerationAdmission> = z.object({
  data: z.object({
    id,
    useCase: z.literal('helpdesk_article_generation'),
    state: z.literal('admitted'),
    taskUuid: id,
    statusUrl: z.string(),
  }),
});
const statusSchema: z.ZodType<ArticleGenerationStatus> = z.object({
  data: z.object({
    taskUuid: id,
    articleGeneration: z
      .object({
        id,
        trigger: z.enum(['manual_button', 'manual_public_reply']),
        state: z.enum(['pending', 'running', 'draft_saved', 'no_change', 'failed', 'unknown']),
        articleId: id.nullable(),
        errorCode: z.string().nullable(),
        updatedAt: z.iso.datetime({ offset: true }),
      })
      .nullable(),
  }),
});

export const articleGenerationApi = {
  title(input: z.infer<typeof titleInput>, idempotencyKey: string) {
    return api.post<ArticleTitleSuggestion>(
      '/api/v1/application-ai-responses',
      { useCase: 'helpdesk_article_title', input: titleInput.parse(input) },
      {
        headers: { 'Idempotency-Key': key.parse(idempotencyKey) },
        schema: titleSchema,
        skipAuthRefresh: true,
      }
    );
  },
  generate(taskUuid: string, idempotencyKey: string) {
    return api.post<ArticleGenerationAdmission>(
      '/api/v1/application-ai-responses',
      { useCase: 'helpdesk_article_generation', input: { taskUuid: id.parse(taskUuid) } },
      {
        headers: { 'Idempotency-Key': key.parse(idempotencyKey) },
        schema: admissionSchema,
        skipAuthRefresh: true,
      }
    );
  },
  status(taskUuid: string, operationId?: string, signal?: AbortSignal) {
    return api.get<ArticleGenerationStatus>(
      `/api/v1/tasks/${encodeURIComponent(id.parse(taskUuid))}`,
      {
        params: {
          view: 'ai-status',
          ...(operationId ? { operationId: id.parse(operationId) } : {}),
        },
        schema: statusSchema,
        signal,
      }
    );
  },
};

export function articleAiError(error: unknown) {
  if (!isApiError(error)) return 'unknown';
  if (error.status === 401 || error.status === 403) return 'denied';
  if (/CONFIGURATION_UNAVAILABLE/u.test(error.code)) return 'unavailable';
  if (/KNOWLEDGE_/u.test(error.code)) return 'knowledgeUnavailable';
  if (/REFUSED|INVALID_OUTPUT/u.test(error.code)) return 'refused';
  if (/PROVIDER_REJECTED/u.test(error.code)) return 'failed';
  if (/STALE/u.test(error.code) || error.status === 412) return 'stale';
  if (!error.status || error.status >= 500 || /UNCERTAIN|UNKNOWN|IN_PROGRESS/u.test(error.code))
    return 'unknown';
  return 'failed';
}
